import { useState, useEffect, useRef } from "react"
import styled from "styled-components"
import { User, Lock, Bell, Eye, Moon, LogOut, Upload, Save, LogIn, Download, Trash2, Shield, Globe, Smartphone, Mail, Camera, MapPin, Link as LinkIcon, Palette, Database, Settings as SettingsIcon, Bot } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import { useNavigate } from "react-router-dom"
import toast from 'react-hot-toast'

const Container = styled.div`
  display: flex;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const Sidebar = styled.div`
  width: 240px;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #eee;
  margin-bottom: 12px;
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .avatar-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0;
    transition: opacity 0.2s;
    cursor: pointer;
  }
  
  &:hover .avatar-overlay {
    opacity: 1;
  }
`

const AvatarUploadInput = styled.input`
  display: none;
`

const UserName = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: ${props => props.theme.textPrimary};
`

const UserEmail = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
`

const SidebarMenu = styled.div`
  padding: 12px 0;
`

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 20px;
  background-color: ${(props) => (props.$active ? props.theme.hoverBackground : "transparent")};
  border: none;
  text-align: left;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  .icon {
    color: ${(props) => (props.$active ? props.theme.buttonPrimary : props.theme.textSecondary)};
  }
  
  .text {
    color: ${(props) => (props.$active ? props.theme.buttonPrimary : props.theme.textPrimary)};
    font-weight: ${(props) => (props.$active ? "600" : "400")};
  }
`

const Content = styled.div`
  flex: 1;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  padding: 20px;
`

const ContentHeader = styled.div`
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ContentTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  color: ${props => props.theme.textPrimary};
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Label = styled.label`
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
`

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  font-size: 14px;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
  
  &::placeholder {
    color: ${props => props.theme.textMuted};
  }
`

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
  
  &::placeholder {
    color: ${props => props.theme.textMuted};
  }
`

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${props => props.theme.border};
    transition: .4s;
    border-radius: 24px;
  }
  
  .slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
  
  input:checked + .slider {
    background-color: ${props => props.theme.buttonPrimary};
  }
  
  input:checked + .slider:before {
    transform: translateX(24px);
  }
`

const ToggleOption = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`

const ToggleText = styled.div`
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
`

const ToggleDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  margin-top: 2px;
`

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
  
  &:hover {
    background-color: ${props => props.theme.buttonPrimaryHover};
  }
  
  &:disabled {
    background-color: ${props => props.theme.textMuted};
    cursor: not-allowed;
  }
`

const AvatarPreview = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 12px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const CoverPreview = styled.div`
  width: 100%;
  max-width: 240px;
  aspect-ratio: 9 / 16;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 12px;
  background: ${props => props.$hasCover
    ? props.theme.borderLight
    : `linear-gradient(135deg, #93a5fd 0%, #b78ad8 100%)`};
  border: 1px dashed ${props => props.theme.border};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

// === 카톡 프로필 편집 스타일 통합 프리뷰 ===
const CombinedPreview = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 5 / 2;
  max-height: 280px;
  border-radius: 16px;
  background: ${props => props.$hasBanner
    ? '#e5e7eb'
    : `linear-gradient(110deg, #b9c9f5 0%, #c5cef0 35%, #d6c5ec 70%, #e3c3df 100%)`};
  border: 1px solid ${props => props.theme.border};
  margin-bottom: 56px; /* 아래 아바타 오버랩 영역 확보 */

  & > img.banner-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 16px;
    display: block;
  }
`

const CombinedAvatarOverlap = styled.div`
  position: absolute;
  left: 50%;
  bottom: -44px;
  transform: translateX(-50%);
  width: 88px;
  height: 88px;
  border-radius: 50%;
  border: 4px solid ${props => props.theme.cardBackground};
  background-color: ${props => props.theme.borderLight};
  background-image: ${props => props.$avatar ? `url(${props.$avatar})` : 'none'};
  background-size: cover;
  background-position: center;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textMuted};
  z-index: 2;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }
`

const CombinedActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;

  & > * { flex: 1; }
`

const UploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${props => props.theme.hoverBackground};
  color: ${props => props.theme.textPrimary};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.theme.borderLight};
  }
`

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.textSecondary};
`

const LoginRequiredMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  
  .icon {
    color: #94a3b8;
    margin-bottom: 16px;
  }
  
  .title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 8px;
  }
  
  .text {
    color: #64748b;
    margin-bottom: 24px;
  }
`

const LoginButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #2563eb;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  
  &:hover {
    background-color: #1d4ed8;
  }
`

// 추가 스타일 컴포넌트
const DangerZone = styled.div`
  border: 1px solid #ef4444;
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
  background-color: #fef2f2;
`

const DangerTitle = styled.h3`
  color: #ef4444;
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
`

const DangerText = styled.p`
  color: #7f1d1d;
  margin: 0 0 16px 0;
  font-size: 14px;
`

const DangerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #dc2626;
  }
  
  &:disabled {
    background-color: #fca5a5;
    cursor: not-allowed;
  }
`

const StatCard = styled.div`
  background-color: ${props => props.theme.hoverBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StatLabel = styled.div`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
`

const StatValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
`

const SettingCard = styled.div`
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: ${props => props.theme.cardBackground};
`

const SettingHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`

const SettingTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
`

const SettingDescription = styled.p`
  margin: 0 0 12px 0;
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  line-height: 1.4;
`

export default function Settings() {
  const { isLoggedIn, user, updateUserProfile, logout } = useAuth();
  const isStoryAdmin = ['admin@antsnest.co.kr', 'dexterity', 'admin'].includes(
    String(user?.email || user?.user_email || user?.username || user?.user_account || '').toLowerCase()
  );
  const { theme, isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalLikes: 0,
    totalComments: 0,
    accountAge: 0
  });
  const [formData, setFormData] = useState({
    username: "",
    full_name: "",
    email: "",
    bio: "",
    location: "",
    website: "",
    avatar_url: "",
    cover_url: "",
    banner_url: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    // 알림 설정
    emailNotifications: true,
    pushNotifications: true,
    likeNotifications: true,
    commentNotifications: true,
    followNotifications: true,
    mentionNotifications: true,
    // 프라이버시 설정
    privateAccount: false,
    showEmail: false,
    showLocation: false,
    allowTagging: true,
    // 화면 설정
    darkMode: false,
    language: "ko",
    // 보안 설정
    twoFactorEnabled: false,
    loginAlerts: true
  });
  
  const fileInputRef = useRef(null);
  const coverFileInputRef = useRef(null);
  const bannerFileInputRef = useRef(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);

  // 사용자 통계 로드
  const fetchUserStats = async () => {
    try {
      const response = await fetch(`/user/stats/${user.id}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setStats({
          totalPosts: data.stats.totalPosts || 0,
          totalLikes: data.stats.totalLikes || 0,
          totalComments: data.stats.totalComments || 0,
          accountAge: Math.floor((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24)) || 0
        });
      }
    } catch (error) {
      console.error('통계 로드 오류:', error);
    }
  };

  // 사용자 데이터 로드
  useEffect(() => {
    if (user) {
      setFormData({
        ...formData,
        username: user.username || "",
        full_name: user.full_name || "",
        email: user.email || "",
        bio: user.bio || "",
        location: user.location || "",
        website: user.website || "",
        avatar_url: user.avatar_url || "",
        cover_url: user.cover_url || "",
        banner_url: user.banner_url || "",
        // 설정 데이터는 기본값 사용 (실제로는 API에서 가져와야 함)
        emailNotifications: user.emailNotifications || true,
        pushNotifications: user.pushNotifications || true,
        likeNotifications: user.likeNotifications !== false,
        commentNotifications: user.commentNotifications !== false,
        followNotifications: user.followNotifications !== false,
        mentionNotifications: user.mentionNotifications !== false,
        privateAccount: user.privateAccount || false,
        showEmail: user.showEmail || false,
        showLocation: user.showLocation || false,
        allowTagging: user.allowTagging !== false,
        darkMode: user.darkMode || false,
        language: user.language || "ko",
        twoFactorEnabled: user.twoFactorEnabled || false,
        loginAlerts: user.loginAlerts !== false
      });
      fetchUserStats();
    }
  }, [user]);

  // 로그인하지 않은 경우 로그인 안내 메시지 표시
  if (!isLoggedIn) {
    return (
      <Container>
        <Content>
          <LoginRequiredMessage>
            <User size={48} className="icon" />
            <div className="title">로그인이 필요합니다</div>
            <div className="text">설정을 변경하려면 로그인이 필요합니다. 로그인 후 다시 시도해주세요.</div>
            <LoginButton onClick={() => navigate('/')}>
              <LogIn size={18} />
              홈페이지로 이동
            </LoginButton>
          </LoginRequiredMessage>
        </Content>
      </Container>
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleAvatarClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCoverClick = () => {
    coverFileInputRef.current?.click();
  };

  const handleCoverFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('이미지 파일만 업로드 가능합니다.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('파일 크기는 10MB 이하여야 합니다.');
      return;
    }
    setCoverFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setCoverPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleBannerClick = () => {
    bannerFileInputRef.current?.click();
  };

  const handleBannerFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('이미지 파일만 업로드 가능합니다.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('파일 크기는 10MB 이하여야 합니다.');
      return;
    }
    setBannerFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setBannerPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 프로필 이미지 업로드 처리
      let updatedAvatarUrl = formData.avatar_url;

      if (avatarFile) {
        const formData = new FormData();
        formData.append('avatar', avatarFile);

        const uploadResponse = await fetch('/user/upload-avatar', {
          method: 'POST',
          credentials: 'include',
          body: formData
        });

        const uploadResult = await uploadResponse.json();

        if (uploadResult.success) {
          updatedAvatarUrl = uploadResult.avatarUrl;
        } else {
          toast.error('프로필 이미지 업로드에 실패했습니다.');
          setLoading(false);
          return;
        }
      }

      // 모바일 배사(cover) 업로드 처리
      let updatedCoverUrl = formData.cover_url;
      if (coverFile) {
        const coverFd = new FormData();
        coverFd.append('files', coverFile);
        const coverUploadRes = await fetch('https://file.skynet.re.kr/awsUpload/send?folder=story_antsnest', {
          method: 'POST',
          body: coverFd
        });
        const coverUploadData = await coverUploadRes.json();
        const newCoverUrl = coverUploadData?.files?.[0]?.url;

        if (newCoverUrl) {
          const updateCoverRes = await fetch('/auth/update-cover', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ userId: user.id, coverUrl: newCoverUrl })
          });
          const updateCoverData = await updateCoverRes.json();
          if (updateCoverData.success) {
            updatedCoverUrl = newCoverUrl;
            setCoverFile(null);
            setCoverPreview(null);
          } else {
            toast.error(updateCoverData.message || '모바일 배경사진 업데이트에 실패했습니다.');
            setLoading(false);
            return;
          }
        } else {
          toast.error('모바일 배경사진 업로드에 실패했습니다.');
          setLoading(false);
          return;
        }
      }

      // 웹 배경(banner) 업로드 처리
      let updatedBannerUrl = formData.banner_url;
      if (bannerFile) {
        const bannerFd = new FormData();
        bannerFd.append('files', bannerFile);
        const bannerUploadRes = await fetch('https://file.skynet.re.kr/awsUpload/send?folder=story_antsnest', {
          method: 'POST',
          body: bannerFd
        });
        const bannerUploadData = await bannerUploadRes.json();
        const newBannerUrl = bannerUploadData?.files?.[0]?.url;

        if (newBannerUrl) {
          const updateBannerRes = await fetch('/auth/update-banner', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ userId: user.id, bannerUrl: newBannerUrl })
          });
          const updateBannerData = await updateBannerRes.json();
          if (updateBannerData.success) {
            updatedBannerUrl = newBannerUrl;
            setBannerFile(null);
            setBannerPreview(null);
          } else {
            toast.error(updateBannerData.message || '웹 배경사진 업데이트에 실패했습니다.');
            setLoading(false);
            return;
          }
        } else {
          toast.error('웹 배경사진 업로드에 실패했습니다.');
          setLoading(false);
          return;
        }
      }
      
      // 프로필 정보 업데이트
      const profileData = {
        username: formData.username,
        full_name: formData.full_name,
        email: formData.email,
        bio: formData.bio,
        location: formData.location,
        website: formData.website,
        avatar_url: updatedAvatarUrl,
        cover_url: updatedCoverUrl,
        banner_url: updatedBannerUrl,
        // 설정에 따라 추가 필드 포함
        emailNotifications: formData.emailNotifications,
        pushNotifications: formData.pushNotifications,
        darkMode: formData.darkMode,
        privateAccount: formData.privateAccount
      };
      
      const result = await updateUserProfile(profileData);

      if (result.success) {
        toast.success('설정이 저장되었습니다.');
        alert('✅ 저장 완료되었습니다.');
      } else {
        toast.error(result.message || '설정 저장에 실패했습니다.');
        alert('❌ 저장 실패: ' + (result.message || '알 수 없는 오류'));
      }
    } catch (error) {
      console.error('설정 저장 오류:', error);
      toast.error('설정 저장 중 오류가 발생했습니다.');
      alert('❌ 저장 중 오류가 발생했습니다.\n' + (error?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('새 비밀번호와 비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch('/user/change-password', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('비밀번호가 변경되었습니다.');
        setFormData({
          ...formData,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        toast.error(result.message || '비밀번호 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      toast.error('비밀번호 변경 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 데이터 내보내기
  const handleExportData = async () => {
    try {
      setLoading(true);
      
      const responses = await Promise.all([
        fetch(`/post/user/${user.id}?limit=1000`, { credentials: 'include' }),
        fetch('/bookmark/list?limit=1000', { credentials: 'include' }),
        fetch(`/follow/followers/${user.id}`, { credentials: 'include' }),
        fetch(`/follow/following/${user.id}`, { credentials: 'include' })
      ]);
      
      const [postsData, bookmarksData, followersData, followingData] = await Promise.all(
        responses.map(response => response.json())
      );
      
      const exportData = {
        user: {
          id: user.id,
          username: user.username,
          email: user.email,
          bio: user.bio,
          created_at: user.created_at,
          stats: stats
        },
        posts: postsData.success ? postsData.posts : [],
        bookmarks: bookmarksData.success ? bookmarksData.bookmarks : [],
        followers: followersData.success ? followersData.followers : [],
        following: followingData.success ? followingData.following : [],
        exportDate: new Date().toISOString()
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `antsnest_data_${user.username}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('데이터 내보내기가 완료되었습니다.');
    } catch (error) {
      console.error('데이터 내보내기 오류:', error);
      toast.error('데이터 내보내기 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  // 계정 삭제
  const handleDeleteAccount = async () => {
    const confirmText = `계정을 삭제하려면 "${user.username}"을 입력하세요:`;
    const userInput = window.prompt(confirmText);
    
    if (userInput !== user.username) {
      toast.error('사용자명이 일치하지 않습니다.');
      return;
    }
    
    if (!window.confirm('정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await fetch('/user/delete', {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('계정이 삭제되었습니다.');
        await logout();
        navigate('/');
      } else {
        toast.error(data.message || '계정 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('계정 삭제 오류:', error);
      toast.error('계정 삭제 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/'); 
    } catch (error) {
      toast.error('로그아웃 중 오류가 발생했습니다.');
    }
  };

  const renderContent = () => {
    if (loading) {
      return <LoadingIndicator theme={theme}>로딩 중...</LoadingIndicator>;
    }
    
    switch (activeSection) {
      case "profile":
        return (
          <>
            <ContentHeader theme={theme}>
              <ContentTitle theme={theme}>프로필 설정</ContentTitle>
            </ContentHeader>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label theme={theme}>프로필 미리보기</Label>
                <CombinedPreview theme={theme} $hasBanner={!!(bannerPreview || formData.banner_url)}>
                  {(bannerPreview || formData.banner_url) && (
                    <img className="banner-img" src={bannerPreview || formData.banner_url} alt="배경사진" />
                  )}
                  <CombinedAvatarOverlap theme={theme} $avatar={avatarPreview || formData.avatar_url}>
                    {!(avatarPreview || formData.avatar_url) && (
                      <User size={32} />
                    )}
                  </CombinedAvatarOverlap>
                </CombinedPreview>
                <CombinedActionRow>
                  <UploadButton theme={theme} type="button" onClick={handleBannerClick}>
                    <Upload size={16} />
                    배경사진 변경
                  </UploadButton>
                  <UploadButton theme={theme} type="button" onClick={handleAvatarClick}>
                    <Upload size={16} />
                    프로필 사진 변경
                  </UploadButton>
                </CombinedActionRow>
                <AvatarUploadInput
                  ref={bannerFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBannerFileChange}
                />
                <AvatarUploadInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </FormGroup>
              <FormGroup>
                <Label theme={theme} htmlFor="full_name">이름</Label>
                <Input theme={theme} type="text" id="full_name" name="full_name" value={formData.full_name} onChange={handleChange} />
              </FormGroup>
              <FormGroup>
                <Label theme={theme} htmlFor="username">아이디 (@멘션)</Label>
                <Input theme={theme} type="text" id="username" name="username" placeholder="영문/숫자/_/- 3~30자" value={formData.username} onChange={handleChange} autoComplete="off" />
              </FormGroup>
              <FormGroup>
                <Label theme={theme} htmlFor="email">이메일</Label>
                <Input theme={theme} type="email" id="email" name="email" value={formData.email} onChange={handleChange} />
              </FormGroup>
              <FormGroup>
                <Label theme={theme} htmlFor="bio">자기소개</Label>
                <Textarea theme={theme} id="bio" name="bio" value={formData.bio} onChange={handleChange} />
              </FormGroup>
              <SaveButton theme={theme} type="submit">
                <Save size={16} />
                저장
              </SaveButton>
            </Form>
          </>
        );
      case "security":
        return (
          <>
            <ContentHeader theme={theme}>
              <ContentTitle theme={theme}>보안 설정</ContentTitle>
            </ContentHeader>
            <Form onSubmit={handlePasswordChange}>
              <FormGroup>
                <Label theme={theme} htmlFor="currentPassword">현재 비밀번호</Label>
                <Input
                  theme={theme}
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                />
              </FormGroup>
              <FormGroup>
                <Label theme={theme} htmlFor="newPassword">새 비밀번호</Label>
                <Input
                  theme={theme}
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                />
              </FormGroup>
              <FormGroup>
                <Label theme={theme} htmlFor="confirmPassword">비밀번호 확인</Label>
                <Input
                  theme={theme}
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
              </FormGroup>
              <SaveButton theme={theme} type="submit">
                <Save size={16} />
                비밀번호 변경
              </SaveButton>
            </Form>
          </>
        );
      case "notifications":
        return (
          <>
            <ContentHeader theme={theme}>
              <ContentTitle theme={theme}>알림 설정</ContentTitle>
            </ContentHeader>
            <Form onSubmit={handleSubmit}>
              <ToggleOption>
                <div>
                  <ToggleText theme={theme}>이메일 알림</ToggleText>
                  <ToggleDescription theme={theme}>이메일로 중요 알림을 받습니다</ToggleDescription>
                </div>
                <ToggleSwitch theme={theme}>
                  <input
                    type="checkbox"
                    name="emailNotifications"
                    checked={formData.emailNotifications}
                    onChange={handleChange}
                  />
                  <span className="slider"></span>
                </ToggleSwitch>
              </ToggleOption>
              <ToggleOption>
                <div>
                  <ToggleText theme={theme}>푸시 알림</ToggleText>
                  <ToggleDescription theme={theme}>브라우저와 앱에서 푸시 알림을 받습니다</ToggleDescription>
                </div>
                <ToggleSwitch theme={theme}>
                  <input
                    type="checkbox"
                    name="pushNotifications"
                    checked={formData.pushNotifications}
                    onChange={handleChange}
                  />
                  <span className="slider"></span>
                </ToggleSwitch>
              </ToggleOption>
              <SaveButton theme={theme} type="submit">
                <Save size={16} />
                저장
              </SaveButton>
            </Form>
          </>
        );
      case "appearance":
        return (
          <>
            <ContentHeader theme={theme}>
              <ContentTitle theme={theme}>화면 설정</ContentTitle>
            </ContentHeader>
            <Form onSubmit={handleSubmit}>
              <ToggleOption>
                <div>
                  <ToggleText theme={theme}>다크 모드</ToggleText>
                  <ToggleDescription theme={theme}>어두운 테마를 사용합니다</ToggleDescription>
                </div>
                <ToggleSwitch theme={theme}>
                  <input
                    type="checkbox"
                    name="darkMode"
                    checked={isDarkMode}
                    onChange={() => toggleTheme()}
                  />
                  <span className="slider"></span>
                </ToggleSwitch>
              </ToggleOption>
              <SaveButton theme={theme} type="submit">
                <Save size={16} />
                저장
              </SaveButton>
            </Form>
          </>
        );
      case "privacy":
        return (
          <>
            <ContentHeader theme={theme}>
              <ContentTitle theme={theme}>개인정보 설정</ContentTitle>
            </ContentHeader>
            <Form onSubmit={handleSubmit}>
              <ToggleOption>
                <div>
                  <ToggleText theme={theme}>비공개 계정</ToggleText>
                  <ToggleDescription theme={theme}>내 게시물을 팔로워에게만 공개합니다</ToggleDescription>
                </div>
                <ToggleSwitch theme={theme}>
                  <input
                    type="checkbox"
                    name="privateAccount"
                    checked={formData.privateAccount}
                    onChange={handleChange}
                  />
                  <span className="slider"></span>
                </ToggleSwitch>
              </ToggleOption>
              <SaveButton theme={theme} type="submit">
                <Save size={16} />
                저장
              </SaveButton>
            </Form>
          </>
        );
      case "account":
        return (
          <>
            <ContentHeader theme={theme}>
              <ContentTitle theme={theme}>계정 관리</ContentTitle>
            </ContentHeader>
            
            {/* 계정 통계 */}
            <StatCard theme={theme}>
              <StatLabel theme={theme}>작성한 게시물</StatLabel>
              <StatValue theme={theme}>{stats.totalPosts}</StatValue>
            </StatCard>
            <StatCard theme={theme}>
              <StatLabel theme={theme}>받은 좋아요</StatLabel>
              <StatValue theme={theme}>{stats.totalLikes}</StatValue>
            </StatCard>
            <StatCard theme={theme}>
              <StatLabel theme={theme}>작성한 댓글</StatLabel>
              <StatValue theme={theme}>{stats.totalComments}</StatValue>
            </StatCard>
            <StatCard theme={theme}>
              <StatLabel theme={theme}>계정 생성일</StatLabel>
              <StatValue theme={theme}>{stats.accountAge}일 전</StatValue>
            </StatCard>

            {/* 위험 구역 */}
            <DangerZone>
              <DangerTitle>위험 구역</DangerTitle>
              <DangerText>
                아래 작업은 신중하게 수행해주세요. 일부 작업은 되돌릴 수 없습니다.
              </DangerText>
              <DangerButton onClick={handleDeleteAccount} disabled={loading}>
                <Trash2 size={16} />
                계정 삭제
              </DangerButton>
            </DangerZone>
          </>
        );
      case "data":
        return (
          <>
            <ContentHeader theme={theme}>
              <ContentTitle theme={theme}>데이터 관리</ContentTitle>
            </ContentHeader>
            
            <SettingCard theme={theme}>
              <SettingHeader>
                <Download size={20} color={theme.textPrimary} />
                <SettingTitle theme={theme}>데이터 내보내기</SettingTitle>
              </SettingHeader>
              <SettingDescription theme={theme}>
                내가 작성한 게시물, 북마크, 팔로워/팔로잉 정보 등을 JSON 파일로 다운로드할 수 있습니다.
              </SettingDescription>
              <SaveButton theme={theme} onClick={handleExportData} disabled={loading}>
                <Download size={16} />
                데이터 내보내기
              </SaveButton>
            </SettingCard>
            
            <SettingCard theme={theme}>
              <SettingHeader>
                <Database size={20} color={theme.textPrimary} />
                <SettingTitle theme={theme}>데이터 통계</SettingTitle>
              </SettingHeader>
              <SettingDescription theme={theme}>
                내 계정과 관련된 데이터 통계입니다.
              </SettingDescription>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <StatCard theme={theme}>
                    <StatLabel theme={theme}>게시물</StatLabel>
                    <StatValue theme={theme}>{stats.totalPosts}</StatValue>
                  </StatCard>
                </div>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <StatCard theme={theme}>
                    <StatLabel theme={theme}>받은 좋아요</StatLabel>
                    <StatValue theme={theme}>{stats.totalLikes}</StatValue>
                  </StatCard>
                </div>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <StatCard theme={theme}>
                    <StatLabel theme={theme}>댓글</StatLabel>
                    <StatValue theme={theme}>{stats.totalComments}</StatValue>
                  </StatCard>
                </div>
              </div>
            </SettingCard>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <Sidebar theme={theme}>
        <SidebarHeader theme={theme}>
          <Avatar>
            <img 
              src={avatarPreview || formData.avatar_url || "/placeholder.svg?height=80&width=80"} 
              alt="User avatar" 
            />
            <div className="avatar-overlay" onClick={handleAvatarClick}>
              <Upload size={20} color="white" />
            </div>
            <AvatarUploadInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
          </Avatar>
          <UserName theme={theme}>{formData.full_name}</UserName>
          <UserEmail theme={theme}>{formData.email}</UserEmail>
        </SidebarHeader>
        <SidebarMenu>
          <MenuItem theme={theme} $active={activeSection === "profile"} onClick={() => setActiveSection("profile")}>
            <User size={18} className="icon" />
            <span className="text">프로필</span>
          </MenuItem>
          <MenuItem theme={theme} $active={activeSection === "security"} onClick={() => setActiveSection("security")}>
            <Lock size={18} className="icon" />
            <span className="text">보안</span>
          </MenuItem>
          <MenuItem theme={theme} $active={activeSection === "notifications"} onClick={() => setActiveSection("notifications")}>
            <Bell size={18} className="icon" />
            <span className="text">알림</span>
          </MenuItem>
          <MenuItem theme={theme} $active={activeSection === "privacy"} onClick={() => setActiveSection("privacy")}>
            <Eye size={18} className="icon" />
            <span className="text">개인정보</span>
          </MenuItem>
          <MenuItem theme={theme} $active={activeSection === "appearance"} onClick={() => setActiveSection("appearance")}>
            <Palette size={18} className="icon" />
            <span className="text">화면</span>
          </MenuItem>
          <MenuItem theme={theme} $active={activeSection === "account"} onClick={() => setActiveSection("account")}>
            <SettingsIcon size={18} className="icon" />
            <span className="text">계정 관리</span>
          </MenuItem>
          <MenuItem theme={theme} $active={activeSection === "data"} onClick={() => setActiveSection("data")}>
            <Database size={18} className="icon" />
            <span className="text">데이터</span>
          </MenuItem>
          {isStoryAdmin && <MenuItem theme={theme} onClick={() => navigate('/settings/agent-personas')}>
            <Bot className="icon" size={20} />
            <span className="text">AI 페르소나 관리</span>
          </MenuItem>}
        </SidebarMenu>
        <SidebarMenu>
          <MenuItem theme={theme} onClick={handleLogout}>
            <LogOut size={18} className="icon" style={{ color: "#ef4444" }} />
            <span className="text" style={{ color: "#ef4444" }}>
              로그아웃
            </span>
          </MenuItem>
        </SidebarMenu>
      </Sidebar>
      <Content theme={theme}>{renderContent()}</Content>
    </Container>
  );
}
